/** CSV で一括登録・一括更新(Modal)。下見 → 本実行(202)→ ジョブをポーリング → 履歴へ。 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Flex, Modal, Select, Space, Steps, Table, Tag, Upload, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { BulkResult, BulkRow } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useIdpBulkJob, useIdpDownload, useInvalidateIdp } from "./hooks";
import {
  BULK_DONE,
  BULK_MODES,
  BULK_RUNNING,
  IMPORT_MAX_BYTES,
  IMPORT_MAX_ROWS,
  ROW_ERROR,
} from "./userOptions";

function importColumns(): ColumnsType<BulkRow> {
  return [
    { title: "行", dataIndex: "line", width: 90 },
    { title: "ユーザー名", dataIndex: "username", width: 200 },
    {
      title: "結果",
      dataIndex: "result",
      width: 110,
      render: (v: string) => <Tag color={v === ROW_ERROR ? "red" : "green"}>{v}</Tag>,
    },
    { title: "内容", width: 320, ellipsis: true, dataIndex: "message" },
  ];
}

export function ImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApi();
  const download = useIdpDownload();
  const invalidate = useInvalidateIdp();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState("create");
  const [dry, setDry] = useState<BulkResult | null>(null);
  // 本実行は 202。ここからはジョブ詳細を 2 秒ごとに見る(R7b fix1 中2)
  const [jobId, setJobId] = useState<number | null>(null);
  const job = useIdpBulkJob(jobId);

  useEffect(() => {
    if (open) {
      setFile(null);
      setMode("create");
      setDry(null);
      setJobId(null);
    }
  }, [open]);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.importIdpUsers(file!, mode, dryRun),
    onSuccess: (res) => {
      if (res.dry_run) {
        setDry(res);
        return;
      }
      setJobId(res.id);       // 202。あとはポーリングで待つ
    },
    onError: (e) => void message.error(errorText(e)),
  });

  // 終わったら(done / failed / aborted)履歴へ送る
  useEffect(() => {
    const done = job.data;
    if (!done || done.status === BULK_RUNNING) return;
    invalidate();
    if (done.status === BULK_DONE) {
      void message.success(`取込が終わった(成功 ${done.ok} / 失敗 ${done.failed})`);
    } else {
      void message.error(`取込が終わらなかった(${done.statusLabel})`);
    }
    setJobId(null);
    onClose();
    navigate("/idp/bulk-jobs");
  }, [job.data, invalidate, navigate, onClose]);

  const running = jobId !== null;
  const step = running ? 3 : dry ? 2 : file ? 1 : 0;

  return (
    <Modal
      open={open}
      title="CSV で一括登録・一括更新"
      width={760}
      onCancel={onClose}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose}>閉じる</Button>
          <Button
            disabled={!file || running}
            loading={run.isPending && !dry}
            onClick={() => {
              setDry(null);
              run.mutate(true);
            }}
          >
            検査する(下見)
          </Button>
          <Button
            type="primary"
            disabled={!dry || running}
            loading={(run.isPending && !!dry) || running}
            onClick={() => run.mutate(false)}
          >
            実行する
          </Button>
        </Space>
      }
    >
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 16 }}
        items={[
          { title: "CSV を選ぶ" },
          { title: "検査する" },
          { title: "実行する" },
          { title: "終わるのを待つ" },
        ]}
      />

      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Flex gap={8} wrap align="center">
          <Button onClick={() => void download("/api/idp/users/import/template.csv")}>
            テンプレート CSV をダウンロード
          </Button>
          <Select
            style={{ width: 260 }}
            value={mode}
            onChange={(v: string) => {
              setMode(v);
              setDry(null);
            }}
            options={BULK_MODES}
          />
        </Flex>

        <Upload.Dragger
          accept=".csv,text/csv"
          maxCount={1}
          beforeUpload={(f) => {
            if (f.size > IMPORT_MAX_BYTES) {
              void message.error("CSV は 2MB まで");
              return Upload.LIST_IGNORE;
            }
            setFile(f);
            setDry(null);
            return false;
          }}
          onRemove={() => {
            setFile(null);
            setDry(null);
          }}
        >
          <p>ここに CSV を置く(UTF-8。{IMPORT_MAX_ROWS} 行・2MB まで)</p>
          <p style={{ color: "var(--ux-text-tertiary)" }}>
            列: username, email, firstName, lastName, enabled, roles(; 区切り), attr:属性名
          </p>
        </Upload.Dragger>

        {running && (
          <Alert
            type="info"
            showIcon
            message={`取込を受け付けた(#${jobId})。終わるまで待つ`}
            description={
              `背後で 1 行ずつ流している(${job.data?.processed ?? 0} / ` +
              `${job.data?.total ?? dry?.total ?? 0} 行)。` +
              "閉じても処理は続き、結果は「一括登録の履歴」に残る。"
            }
          />
        )}

        {dry && (
          <>
            <Alert
              type={dry.failed > 0 ? "warning" : "success"}
              showIcon
              message={`検査の結果: 全 ${dry.total} 行 / 通る ${dry.ok} 行 / 直す ${dry.failed} 行`}
              description={
                dry.failed > 0
                  ? "失敗する行はそのまま飛ばして続ける。直してから入れ直してもよい。"
                  : "このまま実行できる。"
              }
            />
            <Table<BulkRow>
              scroll={{ x: "max-content" }}
              rowKey="line"
              size="small"
              columns={importColumns()}
              dataSource={dry.rows}
              pagination={{ pageSize: 10, showSizeChanger: false }}
            />
          </>
        )}
      </Space>
    </Modal>
  );
}
